import React from 'react';
import { Tag, Tooltip } from 'antd';
import moment from 'moment';
import { useCalendarDay } from '../../controller/holidayCalendar';

const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];

// 只有完整的年月日才標星期與假日；月份、年份、區間字串原樣輸出
const DAY_PATTERNS = [
  /^\d{4}-\d{2}-\d{2}$/,
  /^\d{4}\/\d{2}\/\d{2}$/,
  /^\d{4}\.\d{2}\.\d{2}$/,
  /^\d{2}\/\d{2}\/\d{4}$/,
  /^\d{2}-\d{2}-\d{4}$/,
];

const isDayFormat = (dateStr) => DAY_PATTERNS.some((pattern) => pattern.test(dateStr));

/**
 * 統一成 holidays API 的鍵格式 YYYY-MM-DD。
 *
 * 德安 PMS 回的是 YYYY/MM/DD (Oracle 的 TO_CHAR 格式)，本地資料庫回的是 YYYY-MM-DD。
 * 不轉的話 PMS 來源的每一頁都查不到假日 —— 字串對不上，而且是靜默失敗，
 * 星期照樣顯示、只是標籤永遠不出現。
 */
const toCalendarKey = (value) => {
  if (typeof value !== 'string' || !isDayFormat(value)) return null;
  const parsed = moment(value, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'MM/DD/YYYY', 'MM-DD-YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

/**
 * 日期顯示元件：日期 + 星期，並標示這天在官方日曆上的身分。
 *
 * 判準來自 holidays 表，**不是星期幾** —— 六日照樣可能是春節或和平紀念日
 * (2027-02-06 是週六又是春節)，而補班的週六看起來像週末、實際上要上班。
 * 只看星期幾會把全年最該賣高價與最該賣平日價的兩種日子都判反。
 *
 * 該年度資料還沒載入或載入失敗時只顯示日期與星期，不標任何身分 —— 標示是輔助
 * 資訊，寧可少一個標籤，也不要用星期幾去猜一個可能是錯的。
 *
 * @param {string} date - 日期字串
 */
const DateWithWeekday = ({ date }) => {
  const { category, name, isMakeupWorkday } = useCalendarDay(toCalendarKey(date));

  if (!date) return null;

  const momentDate = moment(date);
  if (!momentDate.isValid() || !isDayFormat(date)) {
    return <span>{date}</span>;
  }

  const weekdayText = WEEKDAYS[momentDate.day()];

  // 三種身分一律用標籤表達，不再用文字顏色 —— 顏色與標籤兩套語言並存時，
  // 讀者得同時記住「紅色代表什麼」和「標籤代表什麼」，而顏色本身還分不出
  // 例假日與國定假日。標籤說得完整，也與假日設定頁的用色一致。
  return (
    <span style={{ whiteSpace: 'nowrap' }}>
      {date} ({weekdayText})
      {category === 'weekend' && <Tag color="red" style={{ marginLeft: 4 }}>例假日</Tag>}
      {category === 'special' && (
        <Tooltip title={name}>
          <Tag color="orange" style={{ marginLeft: 4 }}>{name || '特別假日'}</Tag>
        </Tooltip>
      )}
      {isMakeupWorkday && (
        <Tooltip title="官方公告的補班日，這天要上班">
          <Tag color="blue" style={{ marginLeft: 4 }}>補班</Tag>
        </Tooltip>
      )}
    </span>
  );
};

export default DateWithWeekday;
